import React, {useEffect, useState} from "react";
import Chrome, {ChromeInputType} from "@uiw/react-color-chrome";
import clsx from "clsx";
import {SelectedColor, SelectedColors} from "data/models/survey/colors";
import {Button, Label, Radio} from "flowbite-react";
import {capitalize} from "helpers/capitalize";
import {useRecommendedColors} from "data/access/survey-question-pages/colors";
import {calculateBackgroundColor, calculateColorLight} from "helpers/color-calculator";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown} from "@fortawesome/free-solid-svg-icons";
import "./index.css";
import {traitName} from "data/models/shared/bc-sheet/brand-trait";
import LoadingSurvey from "components/Survey/LoadingSurvey";
import {assertNoError, assertNotNullOrUndefined} from "helpers/asserts";

export type ColorsSelect_SurveyQuestionPage_Props = {
  visible: boolean;
  questionId: string;
  value: SelectedColors | undefined;
  onChange: (id: string, value: SelectedColors) => void;
}

export type PartiallySelectedColors = {
  primary?: SelectedColor;
  secondary?: SelectedColor;
  accent?: SelectedColor;
}

type ColorToSelectProps = {
  index: number;
  colorToSelect: keyof PartiallySelectedColors;
  width: string;
  height: string;
}

export default function ColorsSelectSurveyQuestionPage(
    {
      visible,
      questionId,
      value,
      onChange
    }: ColorsSelect_SurveyQuestionPage_Props
) {
  const [colorChoices, colorChoicesLoading, colorChoicesError] = useRecommendedColors(); // RecommendedColors[]

  const [pickingColor, setPickingColor] = useState<keyof PartiallySelectedColors>("primary");
  const [selectedColor, setSelectedColor] = useState<PartiallySelectedColors>(value ?? {});
  const [selectedColorBox, setSelectedColorBox] = useState<string | null>(
      (value !== undefined
          && selectedColor?.[pickingColor] !== undefined)
          ? `${selectedColor?.[pickingColor]?.trait}.${selectedColor?.[pickingColor]?.color}`
          : null
  );
  const [openedFontsGroup, setOpenedFontsGroup] = useState(1);
  const [showAnimation, setShowAnimation] = useState(true);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowAnimation(false);
    }, 6000);
    return () => clearInterval(timeoutId);
  }, [])

  const colorInFocus = selectedColor?.[pickingColor]?.color;

  function changeSelectedColor(colorSelection: string) {
    const [trait, color] = colorSelection.split(".");
    setSelectedColorBox(colorSelection);
    const newlySelectedColor = {
      ...selectedColor,
      [pickingColor]: {trait, color,},
    };
    setSelectedColor(newlySelectedColor);
    if (["primary", "secondary", "accent"].every(key => key in newlySelectedColor)) {
      assertNotNullOrUndefined(newlySelectedColor.primary);
      onChange(questionId, {
        ...newlySelectedColor,
        background: {
          trait: newlySelectedColor.primary.trait,
          color: calculateBackgroundColor(newlySelectedColor.primary.color),
        },
      } as SelectedColors);
    }
  }

  function handleColorPaletteChange(color: string) {
    changeSelectedColor(`custom.${color}`);
  }

  function changePickingColor(changedValue: keyof PartiallySelectedColors) {
    setPickingColor(changedValue);
    if (selectedColor?.[changedValue] !== undefined) {
      setSelectedColorBox(`${selectedColor?.[changedValue]?.trait}.${selectedColor?.[changedValue]?.color}`)
    } else {
      setSelectedColorBox(null);
    }
  }

  function ColorToSelect({index, colorToSelect, width, height}: ColorToSelectProps) {
    const selectedColorHex = selectedColor[colorToSelect]?.color;
    return <div className={clsx(width, "flex flex-col items-center")}>
      <div className={"w-full text-left ml-1"}>
      <span
          className={"text-neutral-500 text-sm font-bold"}
      >{capitalize(colorToSelect)}</span>
      </div>
      <Radio
          className="hidden peer"
          id={`${questionId}.${colorToSelect}`}
          value={colorToSelect}
          checked={pickingColor === colorToSelect}
          onChange={({target: {value: changedValue}}) => changePickingColor(changedValue as keyof PartiallySelectedColors)}
      />
      <Label
          htmlFor={`${questionId}.${colorToSelect}`}
          className={clsx(
              "w-full inline-flex text-neutral-500 border-3 border-neutral-50 rounded-md cursor-pointer peer-checked:border-neutral-500 peer-checked:text-bc-blue hover:text-neutral-500",
              height,
          )}
          style={{
            ...((showAnimation && value === undefined) ? {
              animationName: `color-highlight`,
              animationDuration: "2s",
              animationDelay: `${index * 1.5}s`,
            } : {}),
          }}
      >
        <div
            className={"grow m-1 rounded-md"}
            style={{
              backgroundColor: selectedColorHex ?? "transparent",
            }}></div>
      </Label>
      <div
          className={"mx-4 mb-2 w-full text-neutral-500 text-sm text-left"}
      >
        {selectedColorHex?.toUpperCase() ?? ""}&nbsp;
      </div>
    </div>
  }

  if (colorChoicesLoading) {
    return visible && <LoadingSurvey /> || null;
  }
  assertNoError(colorChoicesError);

  function handleShowMore() {
    if (openedFontsGroup < (colorChoices?.length ?? 0)) {
      setOpenedFontsGroup(openedFontsGroup + 1);
    }
  }

  return <div className={"flex flex-col w-full"}>
    <div className={"flex flex-col w-full mb-8"}>
      <div className={"my-4 text-center"}>
        <span className={"text-3xl font-bold"}>Choose your brand colors</span>
      </div>
      <div className={"mb-4 text-center"}>
      <span className={"text-xl text-neutral-500"}>
        Your primary color is the most dominant color for your brand.
        Your secondary color should complement it.
        The accent color should be dark or bright.
        Select the swatch at top, then click on a color below.
      </span>
      </div>
      <div className={"w-full max-w-3xl mx-auto border-0 p-4"}>
        <fieldset name={questionId} className="flex flex-row gap-4 justify-center" autoFocus={true}>
          <ColorToSelect index={0} colorToSelect={"primary"} width={"w-1/2"} height={"min-h-32"}/>
          <ColorToSelect index={1} colorToSelect={"secondary"} width={"w-1/4"} height={"min-h-32"}/>
          <ColorToSelect index={2} colorToSelect={"accent"} width={"w-1/4"} height={"min-h-12"}/>
        </fieldset>
      </div>
    </div>
    <div className={"mt-4 text-center"}>
      <span className={"text-3xl font-bold"}>Based on your brand traits, we recommend the following colors.</span>
    </div>
    <fieldset name={questionId} className="w-full flex flex-col mt-8 gap-12" autoFocus={true}>
      {colorChoices?.slice(0, openedFontsGroup).map((colorChoice, index) =>
          <div key={index} className={"flex flex-col"}>
            {colorChoice.traitKey !== 'both' && <div className={"text-center"}>
                <span
                    className={"text-neutral-500 font-bold"}>These colors emphasize: {traitName(colorChoice.traitKey)}</span>
            </div>}
            {colorChoice.traitKey === 'both' && <div className={"text-center"}>
                <span className={"text-neutral-500 font-bold"}>These colors balance all your traits</span>
            </div>}
            <div className={"flex flex-col flex-wrap max-h-96 xl:max-h-72 "}>
              {colorChoice.colors.map((colorToChoose, index) => <div key={index} className={"flex flex-col"}>
                    <div
                        className={"w-36 h-20 border flex flex-col items-center mx-4 mt-2"}
                        style={{
                          borderColor: calculateColorLight(colorToChoose) < 240
                              ? colorToChoose : "#f0f0f0"
                        }}
                    >
                      <Radio
                          className="hidden peer"
                          id={`${colorChoice.traitKey}.${colorToChoose}`}
                          value={`${colorChoice.traitKey}.${colorToChoose}`}
                          checked={selectedColorBox === `${colorChoice.traitKey}.${colorToChoose}`}
                          onChange={({target: {value: changedValue}}) => changeSelectedColor(changedValue)}
                      />
                      <Label
                          htmlFor={`${colorChoice.traitKey}.${colorToChoose}`}
                          className={"w-full h-full inline-flex eyedropper"}
                          style={{backgroundColor: colorToChoose}}
                      />
                    </div>
                    <div
                        key={index}
                        className={"mx-4 mb-2 text-neutral-500 text-sm text-left"}
                    >
                      {colorToChoose}
                    </div>
                  </div>
              )}
            </div>
          </div>
      )}
    </fieldset>
    {openedFontsGroup < (colorChoices?.length ?? 0) && <div className={"mx-auto mt-12"}>
        <Button
            className={"px-12 rounded-lg"}
            color={"gray"}
            onClick={() => handleShowMore()}
        >
            <div className={"flex flex-col"}>
                <div><span className={"text-neutral-500 font-bold"}>Show more</span></div>
                <div><FontAwesomeIcon icon={faChevronDown} size={"2xl"}/></div>
            </div>
        </Button>
    </div>}
    <div className={"my-16 mx-auto"}>
      <div className={"mb-4"}>
        <span className={"text-xl text-neutral-500"}>Or choose a custom color.</span>
      </div>
      <Chrome
          color={colorInFocus}
          inputType={ChromeInputType.HEXA}
          // style={{ marginTop: 10, width: 140 }}
          // placement={GithubPlacement.TopRight}
          showEyeDropper={true}
          showColorPreview={true}
          showEditableInput={true}
          showAlpha={false}
          onChange={(color) => {
            handleColorPaletteChange(color.hex);
          }}
      />
    </div>
  </div>
}
